import { ReportExportProfile } from '../../user/features/profile/types'
import { ReportExportStringsMode } from '../features/extracted-strings/types'
import { ReportSubpageName } from '../types'
import { reportExportStorage } from 'app/modules/auth/helper/storage'

/**
 * Get the default export profile from a list of profiles
 *
 * @param profiles - Array of export profiles
 * @returns The default profile or undefined if not found
 */
export function getDefaultProfile(profiles: ReportExportProfile[]): ReportExportProfile | undefined {
  return profiles.find((profile) => profile.is_default)
}

/**
 * Normalize strings mode string to enum format
 */
function normalizeStringsMode(mode: string): ReportExportStringsMode {
  return mode.toLowerCase().replace(' ', '_') as ReportExportStringsMode
}

/**
 * Get the configured strings limit from the default profile or selected options
 *
 * @param profiles - Array of export profiles
 * @param exportLimit - Default export limit
 * @returns The configured limit as a number
 */
function getConfiguredStringsLimit(profiles: ReportExportProfile[], exportLimit: number): number {
  const exportOptions = reportExportStorage.getExportOptions()

  if (exportOptions) {
    return Number(exportOptions.strings_limit)
  }

  const defaultProfile = getDefaultProfile(profiles)

  if (defaultProfile !== undefined) {
    return Number(defaultProfile.options.strings_limit)
  }

  return exportLimit
}

/**
 * Get the configured strings mode from the default profile or selected options
 *
 * @param profiles - Array of export profiles
 * @param exportMode - Default export mode
 * @returns The configured mode if a profile exists, otherwise the provided default mode
 */
function getConfiguredStringsMode(
  profiles: ReportExportProfile[],
  exportMode: ReportExportStringsMode
): ReportExportStringsMode {
  const exportOptions = reportExportStorage.getExportOptions()

  if (exportOptions) {
    return normalizeStringsMode(exportOptions.strings_mode)
  }

  const defaultProfile = getDefaultProfile(profiles)

  if (defaultProfile !== undefined) {
    return normalizeStringsMode(defaultProfile.options.strings_mode)
  }

  return exportMode
}

/**
 * Get the configured all pages flag from the default profile or selected options
 *
 * @param profiles - Array of export profiles
 * @param defaultAllPages - Default all pages flag
 * @returns The configured all pages flag
 */
function getConfiguredAllPages(profiles: ReportExportProfile[], defaultAllPages: boolean): boolean {
  const exportOptions = reportExportStorage.getExportOptions()

  if (exportOptions) {
    return exportOptions.all_pages
  }

  const defaultProfile = getDefaultProfile(profiles)

  if (defaultProfile !== undefined) {
    return defaultProfile.options.all_pages
  }

  return defaultAllPages
}

/**
 * Get the configured pages from the default profile or selected options
 *
 * @param profiles - Array of export profiles
 * @param defaultPages - Default pages array
 * @returns The configured pages array
 */
function getConfiguredPages(profiles: ReportExportProfile[], defaultPages: ReportSubpageName[]): ReportSubpageName[] {
  const exportOptions = reportExportStorage.getExportOptions()

  if (exportOptions) {
    return exportOptions.pages as ReportSubpageName[]
  }

  const defaultProfile = getDefaultProfile(profiles)

  if (defaultProfile !== undefined) {
    return defaultProfile.options.pages as ReportSubpageName[]
  }

  return defaultPages
}

/**
 * Get the configured export options from storage, profile, or defaults
 *
 * @param profiles - Array of export profiles
 * @param defaults - Default values for export options
 * @returns Object containing limit, mode, allPages, and pages
 */
export function getExportConfig(
  profiles: ReportExportProfile[],
  defaults: {
    stringsLimit: number
    stringsMode: ReportExportStringsMode
    allPages?: boolean
    pages?: ReportSubpageName[]
  }
) {
  return {
    limit: getConfiguredStringsLimit(profiles, Number(defaults.stringsLimit)),
    mode: getConfiguredStringsMode(profiles, defaults.stringsMode),
    allPages: getConfiguredAllPages(profiles, defaults.allPages ?? false),
    pages: getConfiguredPages(profiles, defaults.pages ?? []),
  }
}
